// The language + vocabulary controls, shared by every surface that starts a transcription:
// RecordScreen (live/batch capture), App's import dialog, and RecordingDetail's Transcribe
// banner. One component rather than three copies, because the rules between the two fields are
// where the bugs live — a vocabulary paired with the wrong language is rejected by AWS
// outright, which failed the job and stranded the recording (plan-multilingual.md §1 Bug A).
// Three hand-maintained copies of that rule would drift.
import type { EngineChoice, Vocabulary } from '@scribe/core';
import {
  TRANSCRIPTION_LANGUAGE_OPTIONS,
  selectableVocabularies,
  type TranscriptionLanguage,
} from '../lib/languages';

export function TranscriptionOptions({
  language,
  onLanguageChange,
  vocabularyName,
  onVocabularyChange,
  vocabularies,
  disabled = false,
  deadVocabularyName = null,
  idPrefix,
  engine = 'aws',
  onEngineChange,
  engines = ['aws'],
}: {
  language: TranscriptionLanguage;
  onLanguageChange: (language: TranscriptionLanguage) => void;
  vocabularyName: string;
  onVocabularyChange: (name: string) => void;
  vocabularies: readonly Vocabulary[] | undefined;
  disabled?: boolean;
  /** A vocabulary this recording ASKED for that is no longer usable — deleted, unpublished, or
   *  never registered with AWS. Surfaced rather than silently ignored: the `<select>` would
   *  otherwise fall back to "None" with no explanation, and the re-run would quietly lose the
   *  term bias the original run had. */
  deadVocabularyName?: string | null;
  /** Distinguishes the two <select>s when more than one instance is on a page. */
  idPrefix: string;
  /** WHISPER-1: per-recording engine. The selector renders ONLY when the server offers more
   *  than one (`Me.transcription_engines`) and the caller handles changes — so with the
   *  feature off (the default) this component looks and behaves exactly as before. */
  engine?: EngineChoice;
  onEngineChange?: (engine: EngineChoice) => void;
  engines?: readonly EngineChoice[];
}) {
  const selectable = selectableVocabularies(vocabularies, language);
  const languageId = `${idPrefix}-language`;
  const vocabularyId = `${idPrefix}-vocabulary`;
  const engineId = `${idPrefix}-engine`;
  const showEngine = Boolean(onEngineChange) && engines.length > 1;

  return (
    <>
      <div className="field">
        <label className="label" htmlFor={languageId}>
          Language
        </label>
        <select
          id={languageId}
          value={language}
          onChange={(e) => onLanguageChange(e.target.value as TranscriptionLanguage)}
          disabled={disabled}
        >
          {TRANSCRIPTION_LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="label" htmlFor={vocabularyId}>
          Vocabulary
        </label>
        <select
          id={vocabularyId}
          value={vocabularyName}
          onChange={(e) => onVocabularyChange(e.target.value)}
          disabled={disabled || selectable.length === 0}
        >
          <option value="">None</option>
          {selectable.map((v) => (
            <option key={v.id} value={v.name}>
              {v.name}
            </option>
          ))}
        </select>
        {deadVocabularyName && (
          // A dangling `job_spec.vocabulary_name`. Deleting a vocabulary deliberately leaves
          // existing job specs alone — they record what was REQUESTED — so this is where that
          // decision has to be made visible, or a re-run silently drops the bias.
          <p className="warning small" role="status">
            This recording used <strong>{deadVocabularyName}</strong>, which is no longer
            available. Pick another vocabulary, or continue without one.
          </p>
        )}
        {selectable.length === 0 && (
          // Say WHY it is empty. The list is filtered by language, so "no vocabularies" and
          // "none for this language" look identical otherwise — and the second is actionable.
          <p className="muted small">
            No published vocabularies for this language.
          </p>
        )}
      </div>

      {showEngine && (
        <div className="field">
          <label className="label" htmlFor={engineId}>
            Engine
          </label>
          <select
            id={engineId}
            value={engine}
            onChange={(e) => onEngineChange?.(e.target.value as EngineChoice)}
            disabled={disabled}
          >
            {engines.map((value) => (
              <option key={value} value={value}>
                {ENGINE_LABELS[value]}
              </option>
            ))}
          </select>
          {engine !== 'aws' && (
            <p className="muted small">
              {engine === 'both'
                ? 'AWS stays the main transcript; a Whisper copy is added as a comparison version.'
                : 'Transcribed in-tenant by Whisper. PII redaction is not available.'}
            </p>
          )}
        </div>
      )}
    </>
  );
}

const ENGINE_LABELS: Record<EngineChoice, string> = {
  aws: 'AWS Transcribe',
  whisper: 'Whisper (self-hosted)',
  both: 'Both (compare)',
};
